import { TZDate } from '@date-fns/tz'
import {
	format,
	formatDistanceToNow,
	isToday,
	isValid,
	isYesterday,
	parseISO,
} from 'date-fns'
import { enUS } from 'date-fns/locale/en-US'

import { dateRanges } from '../constants/date'
import { defaultDateFormats } from '../constants/format'
import { localeMap } from '../constants/locale'

import type { SupportedLocales } from '../constants/locale'
import type { Period } from '../constants/period'
import type { Locale } from 'date-fns'

/**
 * Error thrown when an invalid date is passed to a formatting function
 */
export class InvalidDateError extends Error {
	constructor(
		public readonly input: unknown,
		public readonly reason: string,
	) {
		super(`Invalid date: ${reason}`)
		this.name = 'InvalidDateError'
	}
}

/**
 * Format a date based on user's locale
 *
 * @throws {InvalidDateError} if date is invalid
 */
export const formatDate = (
	date: Date | string | number,
	customFormatString?: string,
): string => {
	const dateObj = _toValidDate(date)
	const locale = _getUserLocale()
	const formatPattern =
		customFormatString ?? _userDateFormatOrDefault(locale.code)

	return format(dateObj, formatPattern, { locale })
}

/**
 * Format relative time (e.g., "2 hours ago")
 *
 * @throws {InvalidDateError} if date is invalid
 */
export const formatRelativeTime = (date: Date | string | number): string => {
	const dateObj = _toValidDate(date)
	const locale = _getUserLocale()

	return formatDistanceToNow(dateObj, { addSuffix: true, locale })
}

/**
 * Get user's timezone from browser
 */
export const getUserTimeZone = () => {
	return Intl.DateTimeFormat().resolvedOptions().timeZone
}

/**
 * Convert UTC date to user's local timezone and format
 *
 * Use this when displaying dates from the API (which are always UTC)
 *
 * @throws {InvalidDateError} if date is invalid
 */
export const formatUTCToLocal = (
	utcDate: Date | string | number,
	customFormatString?: string,
): string => {
	const dateObj = _toValidDate(utcDate, { assumeUTC: true })
	const locale = _getUserLocale()
	const userTimeZone = getUserTimeZone()

	// Create a TZDate in the user's timezone
	const tzDate = new TZDate(dateObj, userTimeZone)

	return format(
		tzDate,
		customFormatString ?? _userDateFormatOrDefault(locale.code),
		{ locale },
	)
}

/**
 * Format a date in UTC timezone
 * Takes any date input and displays it as it would appear in UTC
 *
 * @throws {InvalidDateError} if date is invalid
 */
export const formatInUTC = (
	date: Date | string | number,
	customFormatString?: string,
): string => {
	const dateObj = _toValidDate(date)
	const locale = _getUserLocale()

	// Create TZDate in UTC timezone
	const tzDate = new TZDate(dateObj, 'UTC')

	return format(
		tzDate,
		customFormatString ?? _userDateFormatOrDefault(locale.code),
		{ locale },
	)
}

/**
 * Convert a local date to ISO string for API requests
 *
 * Use this when sending dates to the API - it formats the date as an ISO string
 * which represents the same instant in UTC.
 *
 * @param localDate - The local date to convert to UTC ISO string
 *
 * @returns ISO string in UTC (e.g., "2024-01-15T14:30:00.000Z")
 * @throws {InvalidDateError} if date is invalid
 */
export const toUTCISOString = (localDate: Date | string | number): string => {
	const dateObj = _toValidDate(localDate)
	return dateObj.toISOString()
}

/**
 * Get UTC midnight ISO string for a given date
 *
 * Extracts year/month/day from the input and returns UTC midnight as ISO string.
 * Use when user selects a date and API expects UTC start of day.
 *
 * @example
 * // User in UTC-3 selects Sept 3
 * startOfDayUTC(localDate) // => "2024-09-03T00:00:00.000Z"
 *
 * @throws {InvalidDateError} if date is invalid
 */
export const startOfDayUTC = (date: Date | string | number): string => {
	const dateObj = _toValidDate(date)
	const pad = (n: number) => n.toString().padStart(2, '0')

	return `${dateObj.getFullYear()}-${pad(dateObj.getMonth() + 1)}-${pad(dateObj.getDate())}T00:00:00.000Z`
}

/**
 * Parse a UTC ISO string and return a local Date with same year/month/day
 *
 * Use this when displaying a stored UTC date in a date picker.
 * Extracts UTC y/m/d and creates a local date with those values.
 *
 * @example
 * // Stored: "2024-09-03T00:00:00.000Z"
 * // User in UTC-3
 * parseUTCToLocalDate("2024-09-03T00:00:00.000Z") // => local Date for Sept 3
 *
 * @throws {InvalidDateError} if date is invalid
 */
export const parseUTCToLocalDate = (utcString: string): Date => {
	const dateObj = _toValidDate(utcString, { assumeUTC: true })
	// Extract UTC components
	const year = dateObj.getUTCFullYear()
	const month = dateObj.getUTCMonth()
	const day = dateObj.getUTCDate()
	// Create local date with same y/m/d
	return new Date(year, month, day)
}

/**
 * Get UTC end of day ISO string for a given date
 *
 * Extracts year/month/day from the input and returns UTC end of day as ISO string.
 * Use when user selects a date and API expects UTC end of day.
 *
 * @example
 * // User in UTC-3 selects Sept 5
 * endOfDayUTC(localDate) // => "2024-09-05T23:59:59.999Z"
 *
 * @throws {InvalidDateError} if date is invalid
 */
export const endOfDayUTC = (date: Date | string | number): string => {
	const dateObj = _toValidDate(date)
	const pad = (n: number) => n.toString().padStart(2, '0')

	return `${dateObj.getFullYear()}-${pad(dateObj.getMonth() + 1)}-${pad(dateObj.getDate())}T23:59:59.999Z`
}

/**
 * Convert a period to a date range
 * @param period - The period to convert to a date range
 * @param start - The start date of the range
 * @param end - The end date of the range
 */
export const periodToDateRange = (
	period: Period,
	start?: Date,
	end?: Date,
): {
	start: Date
	end: Date
} => {
	switch (period) {
		case 'LAST_7_DAYS':
			return dateRanges.last7Days()
		case 'LAST_30_DAYS':
			return dateRanges.last30Days()
		case 'LAST_60_DAYS':
			return dateRanges.last60Days()
		case 'LAST_90_DAYS':
			return dateRanges.last90Days()
		case 'LAST_180_DAYS':
			return dateRanges.last180Days()
		case 'CUSTOM':
			if (!start || !end) {
				return { start: new Date(), end: new Date() }
			}

			return { start, end }
	}
}

/**
 * Format a date to a month name as MMMM (long)
 *
 * @throws {InvalidDateError} if date is invalid
 */
export const formatDateToMonthName = (date: Date | string | number): string => {
	const dateObj = _toValidDate(date)
	return format(dateObj, 'MMMM', { locale: _getUserLocale() })
}

/**
 * Format a date to a quarter name as Q1, Q2, Q3, Q4
 *
 * @throws {InvalidDateError} if date is invalid
 */
export const formatDateToQuarterName = (
	date: Date | string | number,
): string => {
	const dateObj = _toValidDate(date)
	return format(dateObj, 'Q', { locale: _getUserLocale() })
}

/**
 * Format a timestamp to a readable date string
 *
 * @throws {InvalidDateError} if timestamp is invalid
 */
export const formatGroupTimestamp = (timestamp: string): string => {
	const date = _toValidDate(parseInt(timestamp))

	if (isToday(date)) {
		return `Today at ${format(date, 'h:mm a')}`
	} else if (isYesterday(date)) {
		return `Yesterday at ${format(date, 'h:mm a')}`
	} else {
		return format(date, 'MMM d, yyyy h:mm a')
	}
}

type ToValidDateOptions = {
	/** If true, treats strings without timezone as UTC (adds 'Z') */
	assumeUTC?: boolean
}

/**
 * Parse date input and validate it
 *
 * @throws {InvalidDateError} if date is invalid
 */
function _toValidDate(
	date: Date | string | number,
	options?: ToValidDateOptions,
): Date {
	let dateObj: Date

	if (typeof date === 'string') {
		let normalized = date.trim()

		// If assumeUTC and no timezone indicator, add 'Z'
		if (options?.assumeUTC) {
			const hasTimezone =
				normalized.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(normalized)

			if (!hasTimezone) {
				normalized = normalized.replace(' ', 'T') + 'Z'
			}
		}

		dateObj = parseISO(normalized)
	} else {
		dateObj = new Date(date)
	}

	if (!isValid(dateObj)) {
		throw new InvalidDateError(date, _getInvalidReason(date))
	}

	return dateObj
}

/**
 * Get a human-readable reason why a date is invalid
 */
function _getInvalidReason(input: unknown): string {
	if (input === null || input === undefined) {
		return 'input is null or undefined'
	}

	if (typeof input === 'string') {
		if (input.trim() === '') {
			return 'empty string'
		}
		return `unparsable string "${input}"`
	}

	if (typeof input === 'number' && !Number.isFinite(input)) {
		return `invalid number: ${input}`
	}

	return 'unknown reason'
}

/**
 * Get the user locale or fallback to en-US
 * @param locale - The locale to get the locale for
 */
function _userLocaleOrDefault(locale: string): Locale {
	return localeMap.get(locale as SupportedLocales) ?? enUS
}

/**
 * Get user date format or fallback to MM/dd/yyyy
 * @param locale - The locale to get the date format for
 */
function _userDateFormatOrDefault(locale: SupportedLocales | string): string {
	return defaultDateFormats.get(locale) ?? 'MM/dd/yyyy'
}

/**
 * Check if the locale is supported
 * @param locale - The locale to check if it is supported
 * @returns True if the locale is supported, false otherwise
 */
function _isLocaleSupported(locale: string): locale is SupportedLocales {
	return localeMap.has(locale as SupportedLocales)
}

/**
 * Get user's locale from browser or fallback to en-US
 */
function _getUserLocale(): Locale {
	const browserLocale = _isLocaleSupported(navigator.language)
		? (navigator.language as SupportedLocales)
		: 'en-US'

	return _userLocaleOrDefault(browserLocale)
}
